import { useEffect, useState } from 'react';
import { GET } from '../configs/api';
import { api } from '../utils/constants';
import { resolveAvatarSrc, clearAvatarCache } from '../utils/avatarCache';

/**
 * Resolve a user's cached avatar object URL.
 * Re-downloads only when avatar_s3_key changes (or cache is empty).
 *
 * @param {{ id?: string, avatar_s3_key?: string|null }} user
 * @returns {{ src: string|null, loading: boolean }}
 */
export default function useUserAvatar(user) {
  const [src, setSrc] = useState(null);
  const [loading, setLoading] = useState(false);
  const userId = user?.id;
  const s3Key = user?.avatar_s3_key || null;

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (!userId || !s3Key) {
        if (userId && !s3Key) {
          await clearAvatarCache(userId);
        }
        if (!cancelled) setSrc(null);
        return;
      }

      setLoading(true);
      try {
        const url = await resolveAvatarSrc(userId, s3Key, async () => {
          const res = await GET(api.getUserAvatarDownloadUrl(userId));
          if (res.error || !res.data?.downloadUrl) {
            throw new Error(res.message || 'Failed to get avatar URL');
          }
          return res.data.downloadUrl;
        });
        if (!cancelled) setSrc(url);
      } catch (err) {
        console.error('Avatar resolve failed:', err);
        if (!cancelled) setSrc(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [userId, s3Key]);

  return { src, loading };
}
